import { LitElement, html, css } from 'lit';
import { BootstrapCSS } from '../../resources/css/bootstrap.css';
import { EplibStyles } from '../../resources/css/style.css.js';

export class EplibHomeView extends LitElement {

  static get styles() {
    return [
      BootstrapCSS,
      EplibStyles
    ];
  }

  async firstUpdated() {
    super.firstUpdated();
    document.title = 'EarlyPrint Library';
    document.querySelector("meta[name='description']").setAttribute('content', 'A resource for reading and collaboratively curating a digital library of some 65,000 English books published before 1800');
  }

  render() {
    return html`
    <div class="container-fluid">
      <div class="row justify-content-center">
        <div class="col-md-6">
          <div class="wrapper transparent">
            <div class="row">
              <div class="col-md-12">
                <br />
                <div class="page-header">
                  <h1>EarlyPrint Library</h1>
                </div>
                <hr />
                <p>
                  <b>Welcome to the EarlyPrint Library, a resource for reading and collaboratively
                      curating a digital
                      library of some 66,000 English books published before 1800.</b>
                </p>
              </div>
              <div class="col-md-6">
                <p>
                  The Library is based on transcriptions produced by the
                  <a href="http://www.textcreationpartnership.org" target="_blank">Text Creation
                  Partnership</a> (TCP).
                  Those transcriptions could be better. <b>You can help your fellow scholar-readers
                  by filling in gaps and correcting small errors in the corpus. Please become an
                  EarlyPrint Curator.</b>
                </p>
                <p>
                  Get started by consulting the <a
                  href="https://earlyprint.org/how-to/browse_search_display.html" target="_blank">
                  how to browse, search, and display</a> document, or <b>just click on <a
                  href="/works/">Texts</a> to begin exploring.</b>
                </p>
              </div>
              <div class="col-md-6">
                <img src="https://earlyprint.org/assets/img/lib_screenshot.png"
                      style="max-width: 100%; padding: 8px" />
              </div>
              <div class="col-md-12">
                <hr />
              </div>
            </div>
            <details name="home_accordion">
              <summary>
                <h4 id="headingDocumentation">More Documentation</h4>
              </summary>
              <div id="collapseDocumentation">
                  <p>
                    For more detail on making corrections, consult the documentation pages on
                    <a href="https://earlyprint.org/how-to/howto_correct_gaps.html"
                    target="_blank">textual defects</a>
                    and <a href="https://earlyprint.org/posts/curation-and-quality-assurance.html"
                    target="_blank">curation and
                    quality assurance</a>. For the unique linguistic annotation features of
                    EarlyPrint, see the section on
                    <a href="https://earlyprint.org/posts/linguistic-annotation.html"
                    target="_blank">data enrichment</a>.
                    Over 900 of the texts have matching image sets, primarily from the Thomas
                    Pennant Barton Collection in
                    the Boston Public Library and provided here via an IIIF server at the Internet
                    Archive. For more detail
                    consult the section on <a href="digital-combos.html" target="_blank">digital
                    combos</a>.
                  </p>
              </div>
            </details>
            <details name="home_accordion">
              <summary>
                <h4 id="headingReadMore">More about the Goals of the EarlyPrint Library</h4>
              </summary>
              <div>
                <p>
                  <strong>The EarlyPrint Library aims to create a deduplicated digital library of
                  most English books published before 1700 plus a sampling of mostly
                  eighteenth-century texts
                  from the Evans and ECCO collections.</strong> Each item in the Library will
                  eventually be a complex digital surrogate including:
                </p>
                <ol>
                  <li>
                    <p>A reader provides a transcription that strikes a balance between being
                        faithful to the printed source
                        while being easy to read and use on the laptops and other mobile devices
                        that are the “tables of memory”
                        on which 21st-century scholars do much of their reading and writing.
                    </p>
                  </li>
                  <li>
                    <p>A viewer supplements the reader with good quality page images that
                        provide the witnesses to check the
                        transcriptions and offer modern readers a sense of the materiality of
                        the text in its original embodiment.
                        For more detail on the viewer consult the section on
                        <a href="digital-combos.html" target="_blank">digital combos</a>.
                    </p>
                  </li>
                  <li>
                    <p>Data files provide access to bibliographical, structural, and linguistic
                        metadata that can be used
                        separately or in conjunction to explore particular texts and support
                        forms of “distant” or “scalable”
                        reading across the entire corpus or parts of it.
                    </p>
                  </li>
                  <li>
                    <p>An annotation tool supports collaborative curation and allows users to
                        offer corrections of the most
                        common forms of textual corruption in the transcriptions. This critical
                        feature of the library is
                        implemented on this site and many student- and scholar-curators have
                        already made thousands of
                        corrections.
                    </p>
                  </li>
                </ol>
                <p>You must register before you can add annotations. That is the best way of keeping
                    track of and giving credit
                    to curators. Tell us what is not working. There is nothing like users to
                    discover faulty design or poor
                    implementation, so please let us know how we can make the site better.
                </p>
                <p>
                  <strong>The ultimate goal of our project is to “re-mediate” the Early Modern
                      print heritage,</strong>
                  using the term as Richard Grusin and Jay Bolter did in their excellent book
                  <i>Remediation: Understanding
                      New Media</i> (MIT, 1999). We want to enhance the query potential of the
                  complex digital surrogates that
                  are created by an ensemble of text, image, metadata, and collaborative tools.
                  Surrogates fall short of
                  their originals in some ways but may exceed them in others. This is especially
                  true of digital surrogates.
                  To learn more about our remediation, see Martin Mueller’s account of
                  “<a target="_blank" href="libbuilding.html">What is New About the EarlyPrint
                      Library</a>.”
                </p>
              </div>
            </details>
            <details name="home_accordion">
              <summary>
                <h4 id="headingSubcorpora">More about Subcorpora</h4>
              </summary>
              <div id="collapseSubcorpora">
                <p>Currently the site includes approximately 59,000 texts from EEBO-TCP
                  Phases I and II, and it is our intention to add a few thousand additional
                  EEBO-TCP texts.
                  There are also currently about 4,000 Evans-TCP texts and 2,000 ECCO-TCP texts.
                  As the number of texts on this site has grown, it has become increasingly
                  necessary to filter by areas of interest rather than dealing with a list of tens of
                  thousands of texts. There are a number of filter criteria available, and one of them
                  is "corpus." Currently there are two corpora defined: Drama, with some 860 plays
                  written between 1550 and 1700, and English Civil War, with a multi-genre
                  collection of
                  some 5,900 texts, mostly written between 1640 and 1660. Let us know what other
                  subsets of early printed books you would like to see defined as corpora.
                </p>
                <p>
                  <b>The Drama Corpus.</b> Most of the texts in the drama corpus, and in
                  particular the texts before 1642, have gone through several rounds of
                  curation by three generations of undergraduates. Some 260 of them have
                  matching images. About half of the plays
                  before 1642 are proper digital combos. The majority of the images come from the
                  Thomas Pennant Barton Collection in the Boston Public Library and are mediated over
                  an IIIF server at the Internet Archive. For more detail consult the section on
                  <a href="digital-combos.html" target="_blank">digital combos</a>.
                </p>
                <p>
                  <b>The English Civil War Corpus.</b> The 5,900 texts in this corpus are a
                  hodge-podge, consisting mostly of shorter texts ( &lt; 50,000 words) from the
                  English Civil War (1640-60). Only a few dozen texts are digital combos. They are
                  mainly Civil War pamphlets, but they include <i>Purchas His Pilgrimage</i>, an
                  endless but fascinating ethnographic compilation from the early seventeenth
                  century with an image set from the Newberry Library. This text has undergone
                  substantial curation by a group of undergraduates.
                </p>
              </div>
            </details>
            <br />
            <p>The Editors (contact us at <a href="mailto:editors@earlyprint.org">editors@earlyprint.org</a>):
            </p>
            <div class="row">
              <div class="col-md-1"></div>
              <div class="col-md-3">
                Anupam Basu<br />
                Craig A. Berry<br />
                Philip R. Burns †<br />
              </div>
              <div class="col-md-3">
                Elisabeth Chaghafi<br />
                Doug Knox<br />
                John Ladd<br />
              </div>
              <div class="col-md-3">
                Joe Loewenstein<br />
                Martin Mueller<br />
                Kate Needham<br />
                Steve Pentecost<br />
                <br />
              </div>
              <div class="col-md-2"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <br />
  `;
  }
}

customElements.define('eplib-home-view', EplibHomeView);
